import React, {useEffect, useState} from "react";
import {Helmet} from "react-helmet-async";
import {Link, useNavigate} from "react-router-dom";
import {FormProvider, useForm} from "react-hook-form";

import Button from "../../../component/button/Button";
import MobileMaxWidthWrapper from "../../../component/mobileMaxWidthWrapper/MobileMaxWidthWrapper";
import KakaoLoginButton from "../../../component/kakaoLoginButton/KakaoLoginButton";
import FacebookLoginButton from "../../../component/facebookLoginButton/FacebookLoginButton";
import AppleLoginButton from "../../../component/appleLoginButton/AppleLoginButton";
import FormInput from "../../../component/rhf/formInput/FormInput";

import useRequestPhoneCode from "../../../query/useRequestPhoneCode";
import {useAuthStore} from "../../../store/useAuthStore";
import {homeUrl, signUpUrl} from "../../../util/url";
import {META_TAG_TITLE} from "../../../constants/constants";

import PhoneVerifyStep from "./PhoneVerifyStep/PhoneVerifyStep";

import './Login.css';

const LOGIN_STEP = Object.freeze({
    CREDENTIALS: 'credentials',
    VERIFY: 'verify',
});

/**
 * Login component
 * @returns {JSX.Element} Login component UI.
 */
export default function Login() {
    const methods = useForm({ mode: 'onChange'});
    const { handleSubmit, formState: { isValid }, setError, getValues } = methods;
    const [requestPhoneCode, { called, data, loading, error }] = useRequestPhoneCode();
    const [step, setStep] = useState(LOGIN_STEP.CREDENTIALS);
    const [sessionInfo, setSessionInfo] = useState(null);
    const [resendError, setResendError] = useState('');

    const { login } = useAuthStore();
    const navigate = useNavigate();

    const result = data?.requestSigninByEmailVerification;

    useEffect(() => {
        if (!called || !error) return;
        const serverMessage = error.graphQLErrors?.[0]?.message || error.message;
        if (step === LOGIN_STEP.VERIFY) {
            // verify 단계에서의 실패는 인증번호 재전송 에러로만 표시 (폼 에러로 새지 않게)
            setResendError(serverMessage || '인증번호 재전송에 실패했어요. 다시 시도해주세요.');
        } else {
            setError('password', { message: serverMessage || '이메일 또는 비밀번호가 일치하지 않습니다.' });
            setError('email', { message: '' });
        }
    }, [called, error, step, setError]);

    useEffect(() => {
        if (called && result?.loginSessionToken) {
            setSessionInfo(result);
            setResendError('');
            setStep(LOGIN_STEP.VERIFY);
        }
    }, [called, result]);

    const onSubmit = (values) => {
        handleLogin(values);
    };

    const handleLogin = (values) => {
        requestPhoneCode({ variables: { email: values.email, password: values.password } });
    };

    const handleResend = () => {
        const { email, password } = getValues();
        requestPhoneCode({ variables: { email, password } });
    };

    const handleVerified = async (user) => {
        await login(user);
        navigate(homeUrl());
    };

    return <MobileMaxWidthWrapper className={'max-width-400'}>
        <Helmet>
            <title>로그인 | {META_TAG_TITLE}</title>
            <meta name="description"
                  content="유니크한 빈티지, 세컨핸드부터 구하기 힘든 하이엔드 디자이너 브랜드까지 후루츠에서 한번에." />
        </Helmet>
        {step === LOGIN_STEP.VERIFY && sessionInfo?.loginSessionToken ? (
            <PhoneVerifyStep
                phoneLastFour={sessionInfo.phoneLastFour}
                loginSessionToken={sessionInfo.loginSessionToken}
                expiresAt={sessionInfo.expiresAt}
                resending={loading}
                resendError={resendError}
                onBack={() => {
                    setResendError('');
                    setStep(LOGIN_STEP.CREDENTIALS);
                }}
                onResend={handleResend}
                onSuccess={handleVerified}
            />
        ) : (
            <div className='Login-container'>
                <h1 className='page-title'>로그인</h1>
                <div>
                    <FormProvider {...methods}>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className='Login-email-container'>
                                <FormInput label="이메일" name="email" rules={{ required: true }} />
                            </div>
                            <div className='Login-password-container'>
                                <FormInput label="비밀번호" type="password" name="password" rules={{ required: true }} />
                            </div>
                            <div className='Login-button-container'>
                                <Button type="submit" disabled={!isValid || loading} full={true} text={'로그인'} />
                            </div>
                        </form>
                    </FormProvider>
                </div>
                <div className='Login-other-page-container'>
                    <Link to={signUpUrl()}>회원가입</Link>
                </div>
                <div className='Login-social-container'>
                    <FacebookLoginButton />
                    <KakaoLoginButton />
                    <AppleLoginButton />
                </div>
            </div>
        )}
    </MobileMaxWidthWrapper>
}
